// The screen-reader side of SLoadingOverlay: one polite status region for every overlay on the
// page (WCAG 4.1.3). A region per overlay repeats "Loading" when several start together (route
// loader plus a page overlay), and a category page would carry one per product card. The region
// lives on <body>, so overlays inside inert content (join steps) are still announced. AB#64981
//
// Rules:
// - Only loads still running after LOADING_ANNOUNCE_AFTER_MS are announced: a quick spinner is noise.
// - One announcement covers every overlay loading at once; it clears when the last one stops.
// - When overlays carry different labels, the earliest one still loading is spoken.
// - The region is on the page before its first text, so screen readers have registered it. One
//   created (or recreated) while an announcement is pending gets the full wait again.

export const LOADING_ANNOUNCE_AFTER_MS = 500

const active = new Map<object, () => string>()
let region: HTMLElement | null = null
// Bumped whenever a region is created, so a pending announcement can tell its region is new.
let regionGeneration = 0
let announceTimer: ReturnType<typeof setTimeout> | undefined

/** Puts the region on the page (once, or again if something removed it) and returns it. */
export const ensureLoadingRegion = (): HTMLElement => {
  if (region?.isConnected) {
    return region
  }
  region = document.createElement('div')
  region.setAttribute('role', 'status')
  region.setAttribute('aria-live', 'polite')
  region.setAttribute('data-testid', 'loading-announcer')
  region.className = 'sr-only'
  document.body.appendChild(region)
  regionGeneration++
  return region
}

const announceAfterThreshold = () => {
  const armedFor = regionGeneration
  announceTimer = setTimeout(() => {
    announceTimer = undefined
    // A region created during the wait (or removed and not yet replaced) has not been on the
    // page for the full wait: text written now would not be announced, so wait again.
    if (!region?.isConnected || regionGeneration !== armedFor) {
      ensureLoadingRegion()
      announceAfterThreshold()
      return
    }
    const [earliestLabel] = active.values()
    if (earliestLabel) {
      region.textContent = earliestLabel()
    }
  }, LOADING_ANNOUNCE_AFTER_MS)
}

/** `owner` started loading. Announces after the threshold unless an announcement already covers it. */
export const startLoadingAnnouncement = (owner: object, getLabel: () => string) => {
  active.set(owner, getLabel)
  const target = ensureLoadingRegion()
  if (announceTimer || target.textContent) {
    return
  }
  announceAfterThreshold()
}

/** `owner` stopped loading (or unmounted). Clears the announcement once nothing is loading. */
export const stopLoadingAnnouncement = (owner: object) => {
  if (!active.delete(owner) || active.size) {
    return
  }
  clearTimeout(announceTimer)
  announceTimer = undefined
  if (region) {
    region.textContent = ''
  }
}

/** Test-only: forget every owner, cancel any pending announcement and remove the region. */
export const resetLoadingAnnouncer = () => {
  active.clear()
  clearTimeout(announceTimer)
  announceTimer = undefined
  region?.remove()
  region = null
}
